import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import ZhWords, { ClickWordHandler } from './ZhText'

export interface OtherAnswer {
  id: string
  name: string
  answer: string
  answerContent?: any[]
}

interface OthersAnswersProps {
  answers: OtherAnswer[]
  /** Total answers on the server, which can be more than are loaded. */
  total: number
  loading: boolean
  onLoadAll: () => void
  story: any
  activeIndex: string
  onClickWord: ClickWordHandler
}

const FIRST = 4

const initialOf = (name: string) => (name || '?').trim().charAt(0).toUpperCase() || '?'

/** "How other learners answered": a small reward after sending. No levels, first four, then the rest in place. */
const OthersAnswers: React.FC<OthersAnswersProps> = ({
  answers,
  total,
  loading,
  onLoadAll,
  story,
  activeIndex,
  onClickWord,
}) => {
  const { t } = useTranslation('writeSpeak')
  const zhLang = useChineseLang()
  const [showAll, setShowAll] = useState(false)
  if (!answers.length) return null
  const count = Math.max(total, answers.length)
  const list = showAll ? answers : answers.slice(0, FIRST)
  const more = count - FIRST

  const onMore = () => {
    setShowAll(true)
    if (answers.length < count) onLoadAll()
  }

  return (
    <div className='ws-others-card'>
      <div className='ws-others-top'>
        <div className='ws-label'>{t('others.title')}</div>
        <span className='ws-others-n'>{t('others.answerCount', { count })}</span>
      </div>
      <div className='ws-others-list'>
        {list.map((o, i) => (
          <div className={`ws-other${i >= FIRST ? ' fresh' : ''}`} key={`${o.id}-${i}`}>
            <div className='ws-who'>
              <span className='ws-av' aria-hidden='true'>
                {initialOf(o.name)}
              </span>
              {o.name}
            </div>
            <div className='ws-other-text' lang={zhLang}>
              {o.answerContent?.length ? (
                <ZhWords
                  words={o.answerContent}
                  iterator={`QUESTION-ANSWER-CONTENT-${i}-`}
                  story={story}
                  activeIndex={activeIndex}
                  onClickWord={onClickWord}
                  fontSize='16px'
                />
              ) : (
                o.answer
              )}
            </div>
          </div>
        ))}
      </div>
      {!showAll && more > 0 && (
        <button type='button' className='ws-more' onClick={onMore}>
          {t('others.showMore', { count: more })}
        </button>
      )}
      {showAll && loading && (
        <button type='button' className='ws-more' disabled>
          {t('others.loading')}
        </button>
      )}
    </div>
  )
}

export default OthersAnswers
